import React, { useMemo } from "react";
import ServerErrorModal from "../../../organisms/ServerErrorModal";
import { Button, Col, Row, ListGroup, Badge } from "react-bootstrap";
import styled from "styled-components";
import { HeaderSection } from "../HeaderSection";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { Title } from "../Title";
import { Model } from "../Model";
import { currencyFormat } from "../../../../utils/numberUtils";
import {
  ItemListadoServiciosHotel,
  useListarServiciosHotelPorTipo,
} from "../../../../services/hotelServicios";
import Fechas from "../Fechas/Fechas";
import { useWizard } from "../../../../hooks/wizard";
import SeleccionMascota from "../SeleccionMascota/SeleccionMascota";
import SuccessButton from "../../../organisms/SuccessButton";
import { usePasoReservaHotel } from "../pasoReservaHotel";
import { useQuery } from "@tanstack/react-query";
import {
  CiudadResumida,
  leerCiudadOrigen,
  QUERY_KEY_LEER_CIUDAD_ORIGEN,
} from "../../../../services/paisesServicios";

function DatosBasicos({ model }: { model: Model }) {
  const { avanza, vuelve, volver, avanzar } = useWizard();
  const {
    currentModel,
    definirContrataTransporte,
    definirNochesIndividuales,
    seleccionarPlan,
    definirCiudadOrigen,
  } = usePasoReservaHotel(model);

  const {
    data: serviciosPorTipo,
    isLoading: serviciosPorTipoIsLoading,
    error: serviciosPorTipoError,
    isSuccess: serviciosPorTipoIsSuccess,
  } = useListarServiciosHotelPorTipo();

  const { error: errorCiudadOrigen, isLoading: ciudadOrigenIsLoading } =
    useQuery({
      queryKey: [QUERY_KEY_LEER_CIUDAD_ORIGEN],
      queryFn: async () => await leerCiudadOrigen(),
      enabled: currentModel.contrataTransporte && !currentModel.ciudadOrigen,
      onSuccess: (ciudadOrigenRes: CiudadResumida | undefined) => {
        definirCiudadOrigen({
          ciudadOrigen: ciudadOrigenRes,
        });
      },
    });

  const planes = useMemo(() => {
    if (!serviciosPorTipoIsSuccess || !serviciosPorTipo?.planes) {
      return [];
    }

    let planesRaw: Array<ItemListadoServiciosHotel> = Array.from(
      serviciosPorTipo!.planes
    );
    planesRaw.sort(
      (planA, planB) => (planA.minimoNoches || 0) - (planB.minimoNoches || 0)
    );
    return planesRaw;
  }, [serviciosPorTipoIsSuccess, serviciosPorTipo]);

  const tarifasIndividuales = useMemo(() => {
    if (!serviciosPorTipoIsSuccess || !serviciosPorTipo?.tarifasIndividuales) {
      return [];
    }

    let tarifasIndividualesRaw: Array<ItemListadoServiciosHotel> = Array.from(
      serviciosPorTipo!.tarifasIndividuales
    );
    tarifasIndividualesRaw.sort(
      (tarifaA, tarifaB) =>
        (tarifaA.precioNoche || 0) - (tarifaB.precioNoche || 0)
    );
    return tarifasIndividualesRaw;
  }, [serviciosPorTipoIsSuccess, serviciosPorTipo]);

  const onClickCheckTransporte = () => {
    definirContrataTransporte({
      contrataTransporte: !currentModel.contrataTransporte,
    });
  };

  const onNochesIndividualesChange = (
    val: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nochesIndiv: number = Number(val.target.value);
    if (nochesIndiv > 0) {
      const tarifaDefault =
        tarifasIndividuales.length === 1 ? tarifasIndividuales[0] : undefined;
      const tarifa = currentModel.tarifaNocheIndividual || tarifaDefault;
      const valorTarifa: number = tarifa?.precioNoche || 0;
      definirNochesIndividuales({
        nochesIndividuales: nochesIndiv,
        valorFinalNocheIndividual: valorTarifa,
        tarifaNocheIndividual: tarifa,
      });
    } else {
      definirNochesIndividuales({
        nochesIndividuales: 0,
        valorFinalNocheIndividual: 0,
        tarifaNocheIndividual: undefined,
      });
    }
  };

  const onPlanClick = (plan: ItemListadoServiciosHotel) => {
    seleccionarPlan({ plan });
  };

  const onTarifaIndividualClick = (tarifa: ItemListadoServiciosHotel) => {
    definirNochesIndividuales({
      nochesIndividuales: currentModel.numeroNochesIndividuales,
      valorFinalNocheIndividual: tarifa.precioNoche,
      tarifaNocheIndividual: tarifa,
    });
  };

  const error = serviciosPorTipoError || errorCiudadOrigen;
  const isLoading = serviciosPorTipoIsLoading || ciudadOrigenIsLoading;

  const tarifaFueSeleccionada =
    (currentModel.numeroNochesIndividuales > 0 &&
      currentModel.tarifaNocheIndividual) ||
    currentModel.tarifa;

  const puedeAvanzar = tarifaFueSeleccionada && !isLoading;

  if (error) {
    return <ServerErrorModal />;
  }

  if (avanza) {
    return <Fechas model={currentModel} />;
  }

  if (vuelve) {
    return <SeleccionMascota model={currentModel} />;
  }

  return (
    <Row className="justify-content-center">
      <Col className="col-10">
        <HeaderSection className="pt-2 pb-2 mb-3">
          <Title>
            Datos de agendamiento
            <IconContainer>
              <CalendarMonthIcon sx={{ fontSize: 28 }} />
            </IconContainer>
          </Title>
        </HeaderSection>

        <Row>
          <Row className="mb-4">
            <label
              className="form-label col-8"
              htmlFor="numeroNochesIndividuales"
            >
              Noches individuales:
            </label>
            <div className="col-4">
              <input
                type="number"
                className="form-control"
                id="numeroNochesIndividuales"
                value={currentModel.numeroNochesIndividuales}
                onChange={onNochesIndividualesChange}
                min="0"
                max="3"
              ></input>
            </div>
          </Row>

          {currentModel.numeroNochesIndividuales > 0 &&
            tarifasIndividuales.length > 1 && (
              <Row className="mb-4">
                <div className="mb-2">Seleccione tarifa noche individual:</div>
                {tarifasIndividuales.length > 0 && (
                  <ListGroup as="ul">
                    {tarifasIndividuales.map((tarifa) => (
                      <StyledListGroupItem
                        action
                        onClick={() => {
                          onTarifaIndividualClick(tarifa);
                        }}
                        className="d-flex justify-content-between align-items-start"
                        active={
                          tarifa.id === currentModel.tarifaNocheIndividual?.id
                        }
                        key={tarifa.id}
                      >
                        <div className="ms-2 me-auto">
                          <div className="fw-bold">{tarifa.nombre}</div>
                          <div>{tarifa.descripcion}</div>
                        </div>
                        <div className="ms-2 me-auto"></div>
                        <Badge bg="success" pill>
                          {currencyFormat(tarifa.precioNoche)}
                        </Badge>
                      </StyledListGroupItem>
                    ))}
                  </ListGroup>
                )}
              </Row>
            )}

          <Row className="mb-4">
            <div className="mb-2">Seleccione plan:</div>
            {serviciosPorTipoIsLoading && (
              <div>
                <i>Cargando planes...</i>
              </div>
            )}
            {planes.length > 0 && (
              <ListGroup as="ul">
                {planes.map((plan) => (
                  <StyledListGroupItem
                    action
                    onClick={() => {
                      onPlanClick(plan);
                    }}
                    className="d-flex justify-content-between align-items-start"
                    active={plan.id === currentModel.tarifa?.id}
                    key={plan.id}
                  >
                    <div className="ms-2 me-auto">
                      <div className="fw-bold">{plan.nombre}</div>
                      <div>
                        Noches: {`${plan.minimoNoches} - ${plan.maximoNoches}`}
                      </div>
                      <div>{plan.descripcion}</div>
                    </div>
                    <div className="ms-2 me-auto"></div>
                    <Badge bg="success" pill>
                      {currencyFormat(plan.precioNoche)}
                    </Badge>
                  </StyledListGroupItem>
                ))}
              </ListGroup>
            )}
          </Row>

          <Row className="mb-3">
            <label>
              Contrata transporte:
              <StyledCheck
                type="checkbox"
                className="form-check-input ms-2"
                checked={currentModel.contrataTransporte}
                onChange={onClickCheckTransporte}
              />
            </label>
          </Row>

          <Row className="mt-4 mb-2 justify-content-between">
            <Button style={{ width: "fit-content" }} onClick={volver}>
              <ArrowBackIcon sx={{ fontSize: 20 }} />
              Volver
            </Button>
            <SuccessButton
              type={"button"}
              style={{ width: "fit-content" }}
              disabled={!puedeAvanzar}
              onClick={avanzar}
            >
              Siguiente <ArrowForwardIcon sx={{ fontSize: 20 }} />
            </SuccessButton>
          </Row>
        </Row>
      </Col>
    </Row>
  );
}

const StyledCheck = styled("input")`
  display: inline-block;
  vertical-align: middle;
  margin-top: 0;
`;

const StyledListGroupItem = styled(ListGroup.Item)`
  &.active {
    z-index: 2;
    background-color: ${(props) => props.theme.colors.mainShadow};
    border-color: ${(props) => props.theme.colors.main};
    color: #212529;
  }
`;

const IconContainer = styled("span")`
  display: relative;
  margin-left: 8px;
  color: ${(props) => props.theme.colors.main};
`;

export default DatosBasicos;
